import { CascoPackage } from '@insurance/api/requests'
import { useValidationsField } from '@insurance/hooks'
import { InputParsedNumber } from '@shared/components'
import { CAR_CURRENCY } from '@shared/constants'
import { Col, Form, Input, Modal, Row, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AdditionalEquipment } from './AdditionalEquipment'
import { useCascoInsuranceValues } from './CascoInsuranceContext'
import {
  formProps,
  MAX_ADDITIONAL_EQUIPMENT,
  MAX_INSURANCE_AMOUNT_COEFFICIENT
} from './utils'

type Props = {
  hiddenCost?: boolean
}

export const InsuranceCommonAmount = ({ hiddenCost }: Props) => {
  const { t } = useTranslation()
  const { required } = useValidationsField()
  const {
    insuredAmountProps,
    additionalEquipmentAmountProps,
    additionalEquipmentRootProps,
    costProps,
    insurancePackageProps,
    fiftyFiftyFullProps
  } = formProps(t)
  const formInstance = Form.useFormInstance()
  const [openAdditionalEquipment, setOpenAdditionalEquipment] = useState(false)
  const cost = Form.useWatch(costProps.name, { preserve: true })
  const insurancePackage = Form.useWatch(insurancePackageProps.name, {
    preserve: true
  })
  const fiftyFiftyFull = Form.useWatch(fiftyFiftyFullProps.name, {
    preserve: true
  })
  const { isEditable } = useCascoInsuranceValues()

  const toggleOpenAdditionalEquipment = () =>
    setOpenAdditionalEquipment((open) => !open)

  const onCloseOpenAdditionalEquipment = () => {
    formInstance.validateFields([insuredAmountProps.name])
    toggleOpenAdditionalEquipment()
  }

  const onDeleteOpenAdditionalEquipment = () => {
    formInstance.resetFields([additionalEquipmentRootProps.name])
    onCloseOpenAdditionalEquipment()
  }

  const isAutocompleteCost = insurancePackage === CascoPackage.FIFTY_FIFTY

  useEffect(() => {
    if (!isAutocompleteCost) return

    if (fiftyFiftyFull) {
      formInstance.setFieldValue(insuredAmountProps.name, cost)

      return
    }

    formInstance.setFieldValue(
      insuredAmountProps.name,
      cost ? cost / 2 : undefined
    )
  }, [
    isAutocompleteCost,
    cost,
    formInstance,
    insuredAmountProps.name,
    fiftyFiftyFull
  ])

  return (
    <>
      <Row gutter={[12, 12]}>
        {!hiddenCost && (
          <Col xs={{ span: 24 }} md={{ span: 8 }}>
            <Form.Item {...costProps} rules={[required]}>
              <InputParsedNumber
                min={0}
                disabled={isEditable && isAutocompleteCost}
                placeholder={costProps.label}
              />
            </Form.Item>
          </Col>
        )}

        <Col xs={{ span: 24 }} md={{ span: hiddenCost ? 12 : 8 }}>
          <Form.Item
            {...insuredAmountProps}
            rules={[
              required,
              {
                validator: (_, value) => {
                  const additionalEquipmentAmount =
                    formInstance.getFieldValue(
                      additionalEquipmentAmountProps.name
                    ) || 0

                  if (
                    value <
                    additionalEquipmentAmount / (MAX_ADDITIONAL_EQUIPMENT / 100)
                  )
                    return Promise.reject()

                  return Promise.resolve()
                },
                message: (
                  <>
                    <span>
                      {t('additional_equipment_amount_validation', {
                        value: MAX_ADDITIONAL_EQUIPMENT
                      })}
                    </span>{' '}
                    <Typography.Link
                      strong
                      onClick={toggleOpenAdditionalEquipment}
                    >
                      {t('modify_question')}
                    </Typography.Link>
                  </>
                )
              }
            ]}
          >
            <InputParsedNumber
              min={0}
              max={cost * MAX_INSURANCE_AMOUNT_COEFFICIENT}
              disabled={isAutocompleteCost || !cost}
              placeholder={insuredAmountProps.label}
            />
          </Form.Item>
        </Col>

        <Col xs={{ span: 24 }} md={{ span: hiddenCost ? 12 : 8 }}>
          <Form.Item label={t('currency')}>
            <Input disabled placeholder={t('currency')} value={CAR_CURRENCY} />
          </Form.Item>
        </Col>
      </Row>

      <Modal
        title={t('edit')}
        width={800}
        cancelText={t('close')}
        okText={t('delete')}
        open={openAdditionalEquipment}
        onOk={onDeleteOpenAdditionalEquipment}
        onCancel={onCloseOpenAdditionalEquipment}
      >
        <AdditionalEquipment />
      </Modal>
    </>
  )
}
